import { useState } from 'react';
import { supabase } from '@/src/lib/supabase';

interface EntitlementRow {
  user_id: string;
  email: string;
  plan: string;
  free_used: number;
  paid_used_this_period: number;
  limit: number;
  is_master: boolean;
  paddle_plan?: string;
  paddle_subscription_id?: string;
  updated_at?: string;
}

const PLAN_PRESETS = [
  { label: 'Free (10 gens)', plan: 'free', limit: 10 },
  { label: 'Free Gift (50 gens)', plan: 'free', limit: 50 },
  { label: 'Creator (100 gens/mo)', plan: 'paid', limit: 100 },
  { label: 'Pro (200 gens/mo)', plan: 'paid', limit: 200 },
  { label: 'Master (unlimited)', plan: 'paid', limit: 999999 },
];

export default function Admin() {
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<EntitlementRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [editLimit, setEditLimit] = useState('');
  const [editPlan, setEditPlan] = useState('free');
  const [editMaster, setEditMaster] = useState(false);
  const [allUsers, setAllUsers] = useState<EntitlementRow[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [allLoading, setAllLoading] = useState(false);

  async function lookup() {
    if (!search.trim()) return;
    setLoading(true); setResult(null); setMsg(null);
    try {
      // Search by email in entitlements
      const { data, error } = await supabase
        .from('entitlements')
        .select('*')
        .ilike('email', `%${search.trim()}%`)
        .limit(1)
        .single();

      if (error || !data) {
        // Try looking up user in auth.users via RPC
        setMsg({ type: 'err', text: `No entitlement row found for "${search}". User may not have generated yet.` });
      } else {
        setResult(data);
        setEditLimit(String(data.limit ?? 10));
        setEditPlan(data.plan ?? 'free');
        setEditMaster(data.is_master ?? false);
      }
    } catch (e) {
      setMsg({ type: 'err', text: String(e) });
    }
    setLoading(false);
  }

  async function applyPreset(preset: typeof PLAN_PRESETS[0]) {
    if (!result) return;
    setEditPlan(preset.plan);
    setEditLimit(String(preset.limit));
    setEditMaster(preset.limit >= 999999);
  }

  async function save() {
    if (!result) return;
    setLoading(true); setMsg(null);
    try {
      const { error } = await supabase
        .from('entitlements')
        .update({
          plan: editPlan,
          limit: parseInt(editLimit) || 10,
          is_master: editMaster,
          paid_used_this_period: 0, // reset counter on manual change
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', result.user_id);

      if (error) throw error;
      setMsg({ type: 'ok', text: `✓ Updated ${result.email} — ${editPlan} / ${editLimit} gens / master: ${editMaster}` });
      setResult({ ...result, plan: editPlan, limit: parseInt(editLimit), is_master: editMaster, paid_used_this_period: 0 });
    } catch (e) {
      setMsg({ type: 'err', text: String(e) });
    }
    setLoading(false);
  }

  async function resetUsage() {
    if (!result) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('entitlements')
        .update({ free_used: 0, paid_used_this_period: 0, updated_at: new Date().toISOString() })
        .eq('user_id', result.user_id);
      if (error) throw error;
      setMsg({ type: 'ok', text: `✓ Usage reset for ${result.email}` });
      setResult({ ...result, free_used: 0, paid_used_this_period: 0 });
    } catch (e) {
      setMsg({ type: 'err', text: String(e) });
    }
    setLoading(false);
  }

  async function loadAll() {
    setAllLoading(true);
    const { data } = await supabase
      .from('entitlements')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(100);
    setAllUsers(data || []);
    setShowAll(true);
    setAllLoading(false);
  }

  const used = result ? (result.plan === 'paid' ? result.paid_used_this_period : result.free_used) : 0;
  const usedPct = result ? Math.min(100, Math.round((used / (result.limit || 10)) * 100)) : 0;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 20px', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>⚙️ RaagEngine Admin</h1>
        <p style={{ color: '#666', fontSize: 14 }}>Manage user plans and generation quotas</p>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && lookup()}
          placeholder="Search by email..."
          style={{ flex: 1, padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14 }}
        />
        <button
          onClick={lookup}
          disabled={loading}
          style={{ padding: '10px 20px', background: '#c49a3c', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
        >
          {loading ? '…' : 'Look up'}
        </button>
      </div>

      {/* Message */}
      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14,
          background: msg.type === 'ok' ? '#e8f5e9' : '#ffeaea',
          color: msg.type === 'ok' ? '#2d6a4f' : '#c0392b',
          border: `1px solid ${msg.type === 'ok' ? '#b7dfb8' : '#f5c6c6'}` }}>
          {msg.text}
        </div>
      )}

      {/* User card */}
      {result && (
        <div style={{ border: '1px solid #e0d8cc', borderRadius: 12, padding: 24, marginBottom: 24, background: '#faf7f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{result.email || '—'}</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{result.user_id}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                background: result.is_master ? '#1a1a1a' : result.plan === 'paid' ? '#c49a3c22' : '#e8f0fe',
                color: result.is_master ? '#f0c84a' : result.plan === 'paid' ? '#a07820' : '#3060c0' }}>
                {result.is_master ? '★ MASTER' : result.plan === 'paid' ? (result.paddle_plan?.toUpperCase() || 'PAID') : 'FREE'}
              </span>
            </div>
          </div>

          {/* Usage bar */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>Usage this period</span>
              <span style={{ fontWeight: 600 }}>{used} / {result.is_master ? '∞' : result.limit}</span>
            </div>
            <div style={{ height: 8, background: '#e0d8cc', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${usedPct}%`, background: usedPct > 90 ? '#e74c3c' : '#c49a3c', borderRadius: 4, transition: 'width .3s' }} />
            </div>
          </div>

          {/* Presets */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#555' }}>Quick presets:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PLAN_PRESETS.map(p => (
                <button key={p.label} onClick={() => applyPreset(p)}
                  style={{ padding: '5px 12px', fontSize: 12, border: '1px solid #d4cdbf', borderRadius: 6,
                    background: '#fff', cursor: 'pointer', fontWeight: 500 }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Edit fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
              Plan
              <select value={editPlan} onChange={e => setEditPlan(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
                <option value="free">free</option>
                <option value="paid">paid</option>
              </select>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
              Generation limit
              <input type="number" value={editLimit} onChange={e => setEditLimit(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'flex', flexDirection: 'column' }}>
              Master (unlimited)
              <input type="checkbox" checked={editMaster} onChange={e => setEditMaster(e.target.checked)}
                style={{ marginTop: 10, width: 20, height: 20 }} />
            </label>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={loading}
              style={{ flex: 1, padding: '10px', background: '#c49a3c', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
              {loading ? 'Saving…' : '✓ Save changes'}
            </button>
            <button onClick={resetUsage} disabled={loading}
              style={{ padding: '10px 16px', background: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              Reset usage
            </button>
          </div>
        </div>
      )}

      {/* All users table */}
      <div style={{ borderTop: '1px solid #e0d8cc', paddingTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>All users</h2>
          <button onClick={loadAll} disabled={allLoading}
            style={{ padding: '6px 14px', background: '#f5f0e8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            {allLoading ? 'Loading…' : 'Load latest 100'}
          </button>
        </div>

        {showAll && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f0e8' }}>
                  {['Email', 'Plan', 'Used', 'Limit', 'Master', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0d8cc' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allUsers.map(u => {
                  const uUsed = u.plan === 'paid' ? u.paid_used_this_period : u.free_used;
                  return (
                    <tr key={u.user_id} style={{ borderBottom: '1px solid #f0ebe0' }}>
                      <td style={{ padding: '7px 10px' }}>
                        <button onClick={() => { setSearch(u.email || ''); setResult(u); setEditLimit(String(u.limit)); setEditPlan(u.plan); setEditMaster(u.is_master); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c49a3c', textDecoration: 'underline', fontSize: 13 }}>
                          {u.email || u.user_id.slice(0, 12) + '…'}
                        </button>
                      </td>
                      <td style={{ padding: '7px 10px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                          background: u.is_master ? '#1a1a1a' : u.plan === 'paid' ? '#c49a3c22' : '#f0f0f0',
                          color: u.is_master ? '#f0c84a' : u.plan === 'paid' ? '#a07820' : '#666' }}>
                          {u.is_master ? '★' : u.plan}
                        </span>
                      </td>
                      <td style={{ padding: '7px 10px', color: uUsed >= u.limit ? '#e74c3c' : '#333' }}>{uUsed}</td>
                      <td style={{ padding: '7px 10px' }}>{u.is_master ? '∞' : u.limit}</td>
                      <td style={{ padding: '7px 10px' }}>{u.is_master ? '✓' : '—'}</td>
                      <td style={{ padding: '7px 10px' }}>
                        <button onClick={() => { setSearch(u.email || ''); setResult(u); setEditLimit(String(u.limit)); setEditPlan(u.plan); setEditMaster(u.is_master); window.scrollTo(0, 0); }}
                          style={{ padding: '3px 10px', background: '#fff', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {allUsers.length === 0 && <p style={{ color: '#888', fontSize: 13, padding: '12px 0' }}>No users found.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
